import React from 'react'
import { Button, DropdownMenu, Icon, Tooltip } from '@opallabs/lapidary'
import { decorateServiceName } from '@opallabs/wormhole'
import type { ServiceOption } from '../queries/useSystemServices'
import { channelPickerStyles as styles } from './styles'

// Mirror the moment creator's channel picker (`CreateContentList`): normalize
// the system name through `decorateServiceName` before building the filled
// channel glyph. The bare `gem-channel-<systemName>-filled` rendered blank for
// channels whose system name has no 1:1 glyph (e.g. google_plus, linked_in,
// longform, radio); `decorateServiceName` maps those to a real glyph name.
const channelGlyph = (service: ServiceOption): string =>
  `gem-channel-${decorateServiceName(
    service.iconName || service.systemName,
    'star',
    false,
    true
  )}-filled`

type ChannelPickerProps = {
  services: ServiceOption[]
  selectedServiceId: string | null
  onSelect: (service: ServiceOption) => void
  disabled?: boolean
  onOpen?: () => void
  onClose?: () => void
}

/**
 * Single-select channel picker for the content creator.
 */
export const ChannelPicker: React.FC<ChannelPickerProps> = ({
  services,
  selectedServiceId,
  onSelect,
  disabled,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const selected = services.find((s) => s.id === selectedServiceId) ?? null

  return (
    <DropdownMenu.Root
      open={isOpen}
      onOpenChange={(next) => {
        setIsOpen(next)
        if (next) onOpen?.()
        else onClose?.()
      }}
    >
      <Tooltip
        content={selected ? `Channel: ${selected.name}` : 'Select Channel'}
        side="top"
        zIndex={10000}
      >
        <DropdownMenu.Trigger asChild>
          <Button
            ariaLabel={
              selected ? `Channel: ${selected.name}` : 'Select channel'
            }
            icon={
              <Icon
                ariaHidden
                glyph={selected ? channelGlyph(selected) : 'gem-grid'}
                height={14}
                width={14}
              />
            }
            variant="tertiary"
            disabled={disabled}
            name="channel-picker"
            overrideStyles={styles.button(isOpen, Boolean(disabled))}
            tabIndex={0}
          />
        </DropdownMenu.Trigger>
      </Tooltip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          style={{ minWidth: 220, zIndex: 9999 }}
        >
          {services.map((service) => (
            <DropdownMenu.Item
              key={service.id}
              onSelect={() => onSelect(service)}
              css={styles.item}
            >
              <Icon
                ariaHidden
                glyph={channelGlyph(service)}
                height={16}
                width={16}
              />
              <span>{service.name}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
